import { type MutableRefObject, useMemo, useState, useEffect } from 'react';
import { clampPan, type ContentBounds } from './clampPan.tsx';
import {
	DATA_ATTRIBUTE_DRAG_ID,
	DATA_ATTRIBUTE_DRAG_TYPE,
} from './dataAttributeDragType.constants.tsx';
import type { GLOBAL_LOOPS_CONTAINER_TYPE } from './directionKeys.tsx';
import {
	findClosestDraggableAncestor,
	releasePointerCaptureIfHeld,
	toDiagramPosition,
} from './findClosestDraggableAncestor.tsx';
import { getBoundingClientRect } from './isEveryPropertyDefined.tsx';
import type { ItemReference, ViewBoxRect } from './ListEdgeDetails.tsx';
import { divide, subtract } from './percentageOfLine.tsx';
import { type Point, toPoint } from './toPortInfo.tsx';
import { useLatest } from './useLatest.tsx';

type DragSubjectBase = {
	offset: Point;
};

type DragSubjectPan = DragSubjectBase & {
	type: 'pan';
};

type DragSubjectStatus = DragSubjectBase & {
	type: 'status';
	id: string;
};

type DragSubjectStartNode = DragSubjectBase & {
	type: 'start-node';
	id: string;
};

type DragSubjectPort = DragSubjectBase & {
	type: 'port';
	id: string;
};

type DragSubjectGlobalLoopsContainer = DragSubjectBase & {
	type: typeof GLOBAL_LOOPS_CONTAINER_TYPE;
	id: string;
};

export type DragSubject =
	| DragSubjectPan
	| DragSubjectStatus
	| DragSubjectStartNode
	| DragSubjectPort
	| DragSubjectGlobalLoopsContainer;

type DragDropStateNone = { isDragging: false };

type DragDropStateDragging = {
	isDragging: true;
	dragSubject: DragSubject;
	currentPosition: Point;
};

export type DragDropState = DragDropStateNone | DragDropStateDragging;

export interface Options {
	rootSvgRef: MutableRefObject<HTMLElement | null>;
	zoom: number;
	pan: Point;
	viewBox: ViewBoxRect;
	updatePan: (pan: Point) => void;
	isImmutable: boolean;
	onSelect?: (item: ItemReference | undefined) => void;
	contentBounds?: ContentBounds;
}

/** Provides 'drag drop' functionality for all diagram elements.
 *
 * Drag-drop needs to be handled at the root `<svg>` level in order to
 * get the correct values from mouse events.
 *
 * For drag-drop to work on SVG elements, you must pass the data attributes
 * generated in {link useDraggableDataAttributes}.
 *
 * These data attributes are then used to determine what will be dragged,
 * see {@link findClosestDraggableAncestor} for how.
 *
 * @returns Stateful `dragDropState` object that can be consumed by
 * components or other hooks when dragDrop state changes (e.g. mouse position,
 * what is being dragged etc)
 */
export const useCanvasDragDropEvents = ({
	rootSvgRef,
	zoom,
	viewBox,
	pan,
	updatePan,
	onSelect,
	isImmutable,
	contentBounds,
}: Options): {
	dragDropState: DragDropState;
	temporaryPan: Point | undefined;
} => {
	const [dragSubject, setDragSubject] = useState<DragSubject | undefined>(undefined);
	const [dragDropState, setDragDropState] = useState<DragDropState>({
		isDragging: false,
	});
	const [dragStartPosition, setDragStartPosition] = useState<Point>();
	const [temporaryPan, setTemporaryPan] = useState<Point>();

	const latestPan = useLatest(pan);
	const latestZoom = useLatest(zoom);
	const latestDragSubject = useLatest(dragSubject);
	const latestDragDropState = useLatest(dragDropState);
	const latestViewBox = useLatest(viewBox);
	const latestDragStartPosition = useLatest(dragStartPosition);
	const latestTemporaryPan = useLatest(temporaryPan);
	const latestContentBounds = useLatest(contentBounds);

	useEffect(() => {
		const handlePointerDown = (e: PointerEvent) => {
			const ancestor = findClosestDraggableAncestor(
				// oxlint-disable-next-line typescript/consistent-type-assertions
				e.target as HTMLElement,
				// oxlint-disable-next-line typescript/no-non-null-assertion
				rootSvgRef.current!,
			);

			if (!ancestor) return;

			const dragType = ancestor.getAttribute(DATA_ATTRIBUTE_DRAG_TYPE);
			const dragTargetId = ancestor.getAttribute(DATA_ATTRIBUTE_DRAG_ID) || undefined;

			if (dragType) {
				releasePointerCaptureIfHeld(e);

				const ancestorBounds = getBoundingClientRect(ancestor);

				setDragSubject({
					// oxlint-disable-next-line typescript/consistent-type-assertions, @typescript-eslint/no-explicit-any
					type: dragType as any,
					id: dragTargetId,
					offset: divide(
						toPoint(e.clientX - ancestorBounds.left, e.clientY - ancestorBounds.top),
						latestZoom.current,
					),
				});

				setDragStartPosition(toPoint(e.clientX, e.clientY));
			}
		};

		const handlePointerMove = (e: PointerEvent) => {
			if (!latestDragSubject.current || !latestDragStartPosition.current) {
				return;
			}

			// Check if a drag has been started or it is a standard click
			const hasDragged =
				latestDragDropState.current.isDragging ||
				e.clientX !== latestDragStartPosition.current.x ||
				e.clientY !== latestDragStartPosition.current.y;

			if (!hasDragged) {
				return;
			}

			setDragDropState({
				isDragging: true,
				dragSubject: latestDragSubject.current,
				currentPosition: toDiagramPosition(
					e.clientX,
					e.clientY,
					rootSvgRef.current,
					latestZoom.current,
					latestViewBox.current,
				),
			});

			if (latestDragSubject.current.type === 'pan' && latestDragStartPosition.current) {
				const movement = toPoint(
					e.clientX - latestDragStartPosition.current.x,
					e.clientY - latestDragStartPosition.current.y,
				);
				const zoomedMovement = divide(movement, latestZoom.current);
				const rawPan = subtract(latestPan.current, zoomedMovement);
				const newPan = clampPan(rawPan, latestContentBounds.current, latestViewBox.current);
				setTemporaryPan(newPan);
			}
		};

		const handlePointerUp = (e: PointerEvent) => {
			// unselect current selected item if canvas background is clicked
			if (
				e.target === rootSvgRef.current &&
				!isImmutable &&
				!latestDragDropState.current.isDragging
			) {
				onSelect?.(undefined);
			}

			if (latestDragSubject.current) {
				if (latestTemporaryPan.current) {
					updatePan(latestTemporaryPan.current);
					setTemporaryPan(undefined);
				}

				setDragSubject(undefined);
				setDragDropState({
					isDragging: false,
				});
			}
		};

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.addEventListener('pointerdown', handlePointerDown);

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.addEventListener('pointermove', handlePointerMove);

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.addEventListener('pointerup', handlePointerUp);

		return () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.removeEventListener('pointerdown', handlePointerDown);

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.removeEventListener('pointermove', handlePointerMove);

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.removeEventListener('pointerup', handlePointerUp);
		};
	}, [
		isImmutable,
		latestDragSubject,
		latestViewBox,
		latestZoom,
		onSelect,
		rootSvgRef,
		latestDragDropState,
		latestPan,
		updatePan,
		latestDragStartPosition,
		latestTemporaryPan,
		latestContentBounds,
	]);

	return {
		dragDropState,
		temporaryPan,
	};
};

/** Provides data attributes to inject into SVG elements so that they
 *  can be drag-dropped using `useCanvasDragDropEvents`
 *
 * @example
 * function SomeElement({ id }) {
 *     const dataAttributes = useDraggableDataAttributes('someElement', id);
 *
 *     return (
 *         <g {...dataAttributes}>...</g>
 *     )
 * }
 *
 * // and <SomeElement id='123'> will render in the DOM...
 * <g data-drag-type="someElement" data-drag-id="123">
 */
export const useDraggableDataAttributes = (
	type: DragSubject['type'],
	id?: string,
): {
	'data-drag-type': 'start-node' | 'status' | 'global-loops-container' | 'port' | 'pan';
	'data-drag-id': string | undefined;
} =>
	useMemo(
		() => ({
			[DATA_ATTRIBUTE_DRAG_TYPE]: type,
			[DATA_ATTRIBUTE_DRAG_ID]: id,
		}),
		[id, type],
	);
